import { DirectusFile } from "@directus/sdk";
import {
  Extension,
  FollowTheSigns,
  Inventory,
  Message,
  MoreVert,
  Newspaper,
  UnfoldLess,
  UnfoldMore,
} from "@mui/icons-material";
import HistoryToggleOffIcon from "@mui/icons-material/HistoryToggleOff";
import TimerIcon from "@mui/icons-material/Timer";
import {
  Box,
  Button,
  ButtonGroup,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  LinearProgress,
  Menu,
  MenuItem,
  Paper,
  Stack,
  SxProps,
  Theme,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { Handle, Position } from "@xyflow/react";
import { useMemo, useRef, useState } from "react";
import { NavigateFunction } from "react-router";
import {
  Character,
  DetailedProps,
  Step,
  StepAnalysis,
  StepChallenge,
  StepChallengeItem,
  StepEvent,
  StepMaterial,
  StepMessage,
} from "../../directus/collections";
import { getAssetUrl } from "../../directus/utils";
import {
  useAvgTimeToEnigmaEvent,
  useAvgTimeToEvent,
  useSceneStatsSummary,
} from "../../hooks";
import { MatchFilter } from "../../libs/statsFilters";
import { formatSecondsToDDHHMMSS } from "../../libs/utils";
import { AvailableLangs, LangSwitchOption } from "../../types/misc";
import { formatPercentage } from "../../utils/cityEscapeSummary";
import {
  ChatItem,
  ChatItemCard,
  ChatItemTranslations,
} from "../ChatElementCard.component";
import { ChatItemStatsSubheader } from "../ChatItemStatsSubheader.component";
import EnigmaFlowChart from "../EnigmaFlowChart/EnigmaFlowChart";
import { CardMediaList } from "../PropMediaList.component";
import { SceneExitNodeData, SceneNodeData } from "./types";

export const BASE_NODE_STYLE = {
  padding: 1.5,
  textAlign: "center" as const,
  borderRadius: 2,
  cursor: "pointer",
  display: "flex",
  flexDirection: "column" as const,
  justifyContent: "center",
  alignItems: "center",
};

export type StepChatItem = ChatItem & {
  type: "message" | "challenge" | "materials" | "analysis";
};

const NodeHandles = () => {
  const theme = useTheme();
  return (
    <>
      <Handle
        type="source"
        position={Position.Bottom}
        style={{ background: theme.palette.primary.main }}
      />
      <Handle
        type="target"
        position={Position.Top}
        style={{ background: theme.palette.primary.main }}
      />
    </>
  );
};

export const CtaButton = ({ label }: { label: string }) => {
  const theme = useTheme();
  return (
    <div
      style={{
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        borderRadius: "4px",
        fontWeight: "bold",
        textAlign: "center",
        border: `1px solid ${theme.palette.primary.dark}`,
        cursor: "pointer",
        boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
        flex: 1,
      }}
      dangerouslySetInnerHTML={{ __html: label }}
    ></div>
  );
};

export const PrologueNode = ({ data }: { data: any }) => {
  const theme = useTheme();
  const style = {
    ...BASE_NODE_STYLE,
    minWidth: 160,
    minHeight: 70,
    backgroundColor: theme.palette.success.light,
    border: `2px solid ${theme.palette.success.main}`,
    color: theme.palette.success.contrastText,
  };
  return (
    <Tooltip
      title={
        data.tooltip ? (
          <div dangerouslySetInnerHTML={{ __html: data.tooltip }}></div>
        ) : (
          "Prologo della City Escape"
        )
      }
    >
      <Paper elevation={3} sx={style}>
        <div
          style={{
            fontSize: "14px",
            fontWeight: "bold",
            marginBottom: "4px",
          }}
        >
          <div dangerouslySetInnerHTML={{ __html: data.label }}></div>
          {data.subtitle && (
            <div style={{ fontSize: "10px", opacity: 0.8 }}>
              {data.subtitle}
            </div>
          )}
        </div>
        <NodeHandles />
      </Paper>
    </Tooltip>
  );
};
export const EpilogueNode = ({ data }: { data: any }) => {
  const theme = useTheme();
  const style = {
    ...BASE_NODE_STYLE,
    minWidth: 160,
    minHeight: 70,
    backgroundColor: theme.palette.info.light,
    border: `2px solid ${theme.palette.info.main}`,
    color: theme.palette.info.contrastText,
  };
  return (
    <Tooltip
      title={
        data.tooltip ? (
          <div dangerouslySetInnerHTML={{ __html: data.tooltip }}></div>
        ) : (
          "Epilogo della City Escape"
        )
      }
    >
      <Paper elevation={3} sx={style}>
        <div
          style={{
            fontSize: "14px",
            fontWeight: "bold",
            marginBottom: "4px",
          }}
        >
          <div dangerouslySetInnerHTML={{ __html: data.label }}></div>
          {data.subtitle && (
            <div style={{ fontSize: "10px", opacity: 0.8 }}>
              {data.subtitle}
            </div>
          )}
        </div>
        <NodeHandles />
      </Paper>
    </Tooltip>
  );
};
export const SceneOutputExitNode = ({ data }: { data: SceneExitNodeData }) => {
  const theme = useTheme();
  const label = useMemo(() => {
    const activeTranslation = getActiveTranslation(data.exit, data.activeLang);
    return activeTranslation?.label || "Uscita senza label";
  }, [data.exit, data.activeLang]);
  return (
    <Tooltip
      title={
        data.tooltip ? (
          <div dangerouslySetInnerHTML={{ __html: data.tooltip }}></div>
        ) : (
          "Uscita della scena"
        )
      }
    >
      <Paper
        elevation={3}
        sx={{
          ...BASE_NODE_STYLE,
          minWidth: 440,
          minHeight: 50,
          backgroundColor:
            data.exit.type === "propAvailable"
              ? theme.palette.warning.light
              : theme.palette.primary.dark,
          border: `2px solid ${
            data.exit.type === "propAvailable"
              ? theme.palette.warning.main
              : theme.palette.primary.main
          }`,
          color:
            data.exit.type === "propAvailable"
              ? theme.palette.warning.contrastText
              : theme.palette.secondary.contrastText,
        }}
      >
        <div
          style={{
            fontSize: "12px",
            fontWeight: "bold",
            marginBottom: "4px",
          }}
        >
          <div dangerouslySetInnerHTML={{ __html: label }}></div>
          {/* {data.subtitle && (
            <div style={{ fontSize: "10px", opacity: 0.8 }}>
              {data.subtitle}
            </div>
          )} */}
        </div>
        <NodeHandles />
      </Paper>
    </Tooltip>
  );
};

function formatStepListItemAvatar(character: Character) {
  if (!character?.avatar) return undefined;

  return {
    url: getAssetUrl(import.meta.env.VITE_DIRECTUS_URL, character.avatar.id),
    alt: character.avatar.title || "Character",
  };
}

function formatStepListItemCharacter(
  character: Character | null | undefined,
  lang: LangSwitchOption
): ChatItem["heading"] {
  if (!character) return undefined;

  const activeTranslation = getActiveTranslation(character, lang);

  return {
    title: activeTranslation?.name || "Character",
    avatar: formatStepListItemAvatar(character),
  };
}

export function formatMedia(
  data: DirectusFile | null | undefined
): ChatItem["media"] {
  if (!data) return undefined;
  return {
    type: data.type?.startsWith("image/")
      ? "img"
      : data.type?.startsWith("video/")
      ? "video"
      : "audio",
    url: getAssetUrl(import.meta.env.VITE_DIRECTUS_URL, data.id),
    alt: data.title || "Media",
  };
}

function formatHtmlLinks(content: string) {
  return content.replace("<img ", '<img crossOrigin="anonymous" ');
}

function extractImageSrc(content: string): string {
  const imgTagMatch = content.match(/<img [^>]*src="([^"]+)"[^>]*>/);
  return imgTagMatch ? imgTagMatch[1] : "";
}

function removeImageTags(content: string): string {
  return content.replace(/<img [^>]*>/g, "");
}

type TranslatableItem = {
  translations: Array<{ languages_code: string; [key: string]: any }>;
};

function getActiveTranslation(
  item: TranslatableItem | null | undefined,
  lang: LangSwitchOption
) {
  const translations = item?.translations;
  for (const translation of translations || []) {
    if (translation.languages_code === lang) {
      return translation;
    }
  }

  return undefined;
}

export function formatMessageContent(
  message: StepMessage["item"],
  {
    lang,
    cityEscapeId,
    filters,
  }: { lang: LangSwitchOption; cityEscapeId?: string; filters?: MatchFilter }
): StepChatItem | undefined {
  const activeTranslation = getActiveTranslation(message, lang);

  if (!activeTranslation) {
    return undefined;
  }

  return {
    id: message.id,
    type: "message",
    heading: {
      ...formatStepListItemCharacter(message.character || null, lang),
      description: "Messaggio",
    },
    media: formatMedia(activeTranslation?.media),
    html: activeTranslation?.text,
    cta: activeTranslation?.cta || undefined,
    footer:
      activeTranslation?.cta && cityEscapeId ? (
        <CtaStatsFooter
          cityEscapeId={cityEscapeId}
          step={message}
          filters={filters}
          sx={{ justifyContent: "center" }}
        />
      ) : undefined,
    more: [
      {
        label: "Vedi su Admin",
        onClick: () => {
          const url = new URL(
            `admin/content/steps_messages/${message.id}`,
            import.meta.env.VITE_DIRECTUS_URL
          );
          window.open(url.toString(), "_blank");
        },
      },
    ],
  };
}

export function EnigmaStatsSubheader({
  challenge,
  cityEscapeId,
  filters,
}: {
  challenge: StepChallenge;
  filters?: MatchFilter;
  cityEscapeId?: string;
}) {
  const endStats = useAvgTimeToEnigmaEvent(
    { eventType: ["challenge_requested_solution", "challenge_solution"] },
    cityEscapeId,
    challenge.id,
    filters,
    { traceKey: "EnigmaStatsSubheader::endStats", verbose: false }
  );

  const startStats = useAvgTimeToEnigmaEvent(
    {},
    cityEscapeId,
    challenge.id,
    filters,
    { traceKey: "EnigmaStatsSubheader::startStats", verbose: false }
  );

  return (
    <Box>
      <Typography variant="subtitle1">Enigma</Typography>
      <ChatItemStatsSubheader
        isLoading={endStats.isLoading || startStats.isLoading}
        playRate={startStats.data?.playRate ?? 0}
        avgTimeFromStart={startStats.data?.avgRelativeTime}
        avgDuration={endStats.data?.avgRelativeTime}
      />
    </Box>
  );
}

export function formatChallengeContent(
  challenge: StepChallenge,
  {
    cityEscapeId,
    filters,
    lang,
    openEnigmaFlowChart,
  }: {
    cityEscapeId?: string;
    filters?: MatchFilter;
    lang: LangSwitchOption;
    openEnigmaFlowChart?: (challenge: StepChallenge) => void;
  }
): StepChatItem | undefined {
  // console.debug("StepChallenge data:", challenge);
  const activeTranslation = getActiveTranslation(challenge, lang);

  if (!activeTranslation) {
    return undefined;
  }

  return {
    id: challenge.id,
    type: "challenge",
    heading: {
      ...formatStepListItemCharacter(challenge.character || null, lang),
      subheader: (
        <EnigmaStatsSubheader
          challenge={challenge}
          cityEscapeId={cityEscapeId}
          filters={filters}
        />
      ),
      // description: "Enigma",
    },
    html: activeTranslation?.text,
    media: formatMedia(activeTranslation?.media),
    more: [
      {
        label: "Vedi dettagli",
        onClick: () => {
          if (openEnigmaFlowChart) {
            openEnigmaFlowChart(challenge);
          }
        },
      },
      {
        label: "Vedi su Admin",
        onClick: () => {
          const url = new URL(
            `admin/content/steps_challenge/${challenge.id}`,
            import.meta.env.VITE_DIRECTUS_URL
          );

          window.open(url.toString(), "_blank");
        },
      },
      {
        label: "Vai a statistiche complete",
        onClick: ({ navigate }: { navigate: NavigateFunction }) => {
          if (navigate) {
            navigate({
              pathname: `/enigmas`,
              search: `?ce=${cityEscapeId || ""}&enigma=${challenge.id}`,
            });
          }
        },
      },
    ],
    onCardClick: () => {
      if (openEnigmaFlowChart) {
        openEnigmaFlowChart(challenge);
      }
    },
  };
}

export function formatEventMaterialContent(
  event: StepMaterial["item"],
  { lang, props }: { lang: LangSwitchOption; props?: DetailedProps[] }
): StepChatItem | undefined {
  console.debug("StepEvent Material data:", event, props);
  if (props?.length === 1) {
    const detailedProp = props[0];
    const activeTranslation = getActiveTranslation(detailedProp, lang);
    const partner = detailedProp.partners?.[0];

    return {
      id: event.id,
      type: "materials",
      heading: {
        title: partner ? `${partner.name}` : "Nessun partner",
        description: `Sblocco: ${
          activeTranslation?.name || "materiale di gioco"
        }`,
        avatar: { icon: <Inventory /> },
      },
      content: (
        <CardMediaList
          media={detailedProp.media}
          imageListProps={{
            sx: { width: "100%" },
            cols: 3,
            rowHeight: 164,
          }}
        />
      ),
      footer: (
        <Typography variant="h6">
          {activeTranslation?.name || "materiale di gioco"}
        </Typography>
      ),
    };
  }
}

export function formatEventAnalysisContent(
  event: StepAnalysis["item"],
  { lang }: { lang: LangSwitchOption }
): StepChatItem | undefined {
  const activeTranslation = getActiveTranslation(event.indepth_analysis, lang);

  if (!activeTranslation) {
    return undefined;
  }

  const mediaUrl = extractImageSrc(activeTranslation?.content || "");

  if (
    activeTranslation?.content &&
    !mediaUrl &&
    activeTranslation.content.includes("<img")
  ) {
    throw new Error("Image tag found but no src extracted");
  }

  const contentWithoutImages = removeImageTags(
    activeTranslation?.content || ""
  );

  return {
    id: event.id,
    type: "analysis",
    heading: {
      title: activeTranslation?.title || "In-depth Analysis",
      description: "Sblocco approfondimento",
      avatar: { icon: <Newspaper /> },
    },
    html: contentWithoutImages,
    media: mediaUrl
      ? {
          type: "img",
          url: mediaUrl,
          alt: activeTranslation?.title || "In-depth Analysis Image",
        }
      : undefined,
  };
}

export function formatEventContent(
  item: StepEvent["item"],
  { lang, props }: { lang: LangSwitchOption; props?: DetailedProps[] }
): StepChatItem | undefined {
  let chatItem: StepChatItem | undefined;
  if (item && item.type === "analysis") {
    chatItem = formatEventAnalysisContent(item as StepAnalysis["item"], {
      lang,
    });
  } else {
    chatItem = formatEventMaterialContent(item as StepMaterial["item"], {
      lang,
      props,
    });
  }
  if (!chatItem) return undefined;

  chatItem.more = [
    ...(chatItem.more || []),
    {
      label: "Vedi su Admin",
      onClick: () => {
        const url = new URL(
          `admin/content/steps_event/${item.id}`,
          import.meta.env.VITE_DIRECTUS_URL
        );
        window.open(url.toString(), "_blank");
      },
    },
  ];

  return chatItem;
}

export const stepFormatters: Record<
  Step["collection"],
  (
    data: any,
    {
      lang,
    }: {
      cityEscapeId?: string;
      lang: LangSwitchOption;
      props?: DetailedProps[];
      filters?: MatchFilter;
      openEnigmaFlowChart?: (challenge: StepChallenge) => void;
    }
  ) => StepChatItem | undefined
> = {
  steps_messages: formatMessageContent,
  steps_challenge: formatChallengeContent,
  steps_event: formatEventContent,
};

export type StepTranslation = {
  id: string;
} & Record<AvailableLangs, StepChatItem>;

export function formatSceneSteps(
  steps: Step[],
  {
    cityEscapeId,
    props,
    filters,
    openEnigmaFlowChart,
  }: {
    cityEscapeId?: string;
    props?: DetailedProps[];
    openEnigmaFlowChart?: (challenge: StepChallenge) => void;
    filters?: MatchFilter;
  }
) {
  const formattedSteps: StepTranslation[] = [];
  for (const step of steps) {
    const formatter = stepFormatters[step.collection];
    const translations = { id: step.id } as StepTranslation;
    if (formatter) {
      for (const _lang of ["it-IT", "en-US"] as AvailableLangs[]) {
        const formattedStep = formatter(step.item, {
          lang: _lang,
          props,
          openEnigmaFlowChart,
          cityEscapeId,
          filters,
        });
        if (formattedStep) {
          translations[_lang] = formattedStep;
        }
      }
    }
    formattedSteps.push(translations);
  }
  return formattedSteps;
}

export function CtaStatsFooter({
  cityEscapeId,
  step,
  filters,
  sx,
}: {
  cityEscapeId: string;
  filters?: MatchFilter;
  step: StepMessage["item"] | StepChallengeItem["item"] | StepEvent["item"];
  sx?: SxProps<Theme>;
}) {
  const stats = useAvgTimeToEvent(
    { eventType: "cta" },
    cityEscapeId,
    step.id,
    filters,
    { traceKey: "CtaStatsFooter", verbose: false }
  );
  return (
    <ChatItemStatsSubheader
      isLoading={stats.isLoading}
      playRate={stats.data?.playRate ?? 0}
      avgTimeFromStart={stats.data?.avgRelativeTime}
      sx={sx}
    />
  );
}
export const ExpandableSceneNode = ({ data }: { data: SceneNodeData }) => {
  const theme = useTheme();
  const [showedEnigma, setShowedEnigma] = useState<StepChallenge | null>(null);
  const dialogRef = useRef<HTMLDialogElement>(null);

  const stepsTranslations = useMemo(() => {
    const items = formatSceneSteps(data.scene.steps, {
      cityEscapeId: data.scene.city_escape,
      filters: data.filters,
      props: data.props,
      openEnigmaFlowChart: (challenge: StepChallenge) => {
        setShowedEnigma(challenge);
        dialogRef.current?.showModal();
      },
    });
    return items;
  }, [data.scene.steps, data.props]);

  const steps = useMemo(() => {
    if (data.activeLang === "both") {
      return stepsTranslations.map((step) => step["it-IT"]);
    } else {
      return stepsTranslations.map(
        (step) => step[data.activeLang as AvailableLangs]
      );
    }
  }, [stepsTranslations, data.activeLang]);

  const sceneTitle = useMemo(() => {
    const activeTranslation = getActiveTranslation(data.scene, data.activeLang);
    return activeTranslation?.name || "Scena senza titolo";
  }, [data.scene, data.activeLang]);

  const stats = useSceneStatsSummary(
    data.scene.city_escape,
    data.scene.id,
    data.filters
  );

  const stepsSummary = useMemo(() => {
    const messages = steps.filter((step) => step.type === "message");
    const challenges = steps.filter((step) => step.type === "challenge");
    const analyses = steps.filter((step) => step.type === "analysis");
    const materials = steps.filter((step) => step.type === "materials");

    const tooltipContent = (items: StepChatItem[]) => (
      <Stack direction="column" spacing={1} style={{ zoom: 0.6 }}>
        {items.map((item) => (
          <ChatItemCard key={`${data.activeLang}-${item.id}`} item={item} />
        ))}
      </Stack>
    );

    return {
      // total: data.scene.steps.length,
      messages: {
        icon: <Message fontSize="small" />,
        count: messages.length,
        tooltip: tooltipContent(messages),
      },
      challenges: {
        icon: <Extension fontSize="small" />,
        count: challenges.length,
        tooltip: tooltipContent(challenges),
      },
      analyses: {
        icon: <Newspaper fontSize="small" />,
        count: analyses.length,
        tooltip: tooltipContent(analyses),
      },
      materials: {
        icon: <Inventory fontSize="small" />,
        count: materials.length,
        tooltip: tooltipContent(materials),
      },
    };
  }, [data.scene.steps]);

  const [moreAnchorEl, setMoreAnchorEl] = useState<null | HTMLElement>(null);

  const sceneSubtitle = useMemo(() => {
    if (stats.isLoading) return <LinearProgress />;
    if (!stats.data) return null;
    return (
      <Stack direction="row" spacing={2} alignItems="center">
        <Tooltip title="Percentuale di partite in cui si arriva a questa scena">
          <Stack direction="row" spacing={0.75} alignItems="center">
            <FollowTheSigns
              fontSize="small"
              style={{ verticalAlign: "middle" }}
            />
            <Typography>{formatPercentage(stats.data.playRate)}</Typography>
          </Stack>
        </Tooltip>
        <span>{" | "}</span>
        <Tooltip title="Tempo medio di arrivo a questa scena">
          <Stack direction="row" spacing={0.75} alignItems="center">
            <HistoryToggleOffIcon
              fontSize="small"
              style={{ verticalAlign: "middle" }}
            />
            <Typography>
              {formatSecondsToDDHHMMSS(stats.data.avgTimeFromStart, {
                first: 3,
              })}
            </Typography>
          </Stack>
        </Tooltip>
        <span>{" | "}</span>
        <Tooltip title="Durata media trascorsa nella scena">
          <Stack direction="row" spacing={0.75} alignItems="center">
            <TimerIcon fontSize="small" style={{ verticalAlign: "middle" }} />
            <Typography>
              {formatSecondsToDDHHMMSS(stats.data.avgDuration, { first: 2 })}
            </Typography>
          </Stack>
        </Tooltip>
      </Stack>
    );
  }, [stats.data, stats.isLoading]);

  return (
    <Card
      variant="outlined"
      sx={{
        width: data.activeLang === "both" ? 1000 : 500,
        minHeight: 70,
      }}
    >
      <CardHeader
        title={<Typography variant="h5">{sceneTitle}</Typography>}
        subheader={sceneSubtitle}
        action={
          <ButtonGroup>
            <IconButton>
              {data.isExpanded ? (
                <UnfoldLess onClick={data.collapseCard} />
              ) : (
                <UnfoldMore onClick={data.expandCard} />
              )}
            </IconButton>
            <IconButton onClick={(e) => setMoreAnchorEl(e.currentTarget)}>
              <MoreVert />
            </IconButton>
            <Menu
              id="long-menu"
              anchorEl={moreAnchorEl}
              open={Boolean(moreAnchorEl)}
              onClose={() => setMoreAnchorEl(null)}
            >
              {
                <MenuItem
                  onClick={() => {
                    const path = `admin/content/scenes/${data.scene.id}`;
                    const url = new URL(
                      path,
                      import.meta.env.VITE_DIRECTUS_URL
                    );
                    window.open(url.toString(), "_blank");
                    setMoreAnchorEl(null);
                  }}
                >
                  Vedi su Admin
                </MenuItem>
              }
            </Menu>
          </ButtonGroup>
        }
      ></CardHeader>
      <CardContent
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "stretch",
        }}
      >
        {(data.isExpanded && (
          <Stack spacing={2} direction="column">
            {stepsTranslations.map((translations) => {
              return (
                <ChatItemTranslations
                  key={translations.id}
                  item={translations}
                  theme={theme}
                  activeLang={
                    data.activeLang === "both" ? undefined : data.activeLang
                  }
                />
              );
            })}
          </Stack>
        )) || (
          <Stack direction="row" spacing={4} alignItems="center">
            {Object.entries(stepsSummary)
              .filter(([key, value]) => value.count > 0)
              .map(([key, value]) => (
                <Tooltip
                  key={key}
                  title={value.tooltip || ""}
                  placement="right"
                >
                  <Box key={key} display="flex" alignItems="center">
                    {value.icon}: {value.count}
                  </Box>
                </Tooltip>
              ))}
          </Stack>
        )}
      </CardContent>

      {/* Handle per permettere connessioni agli edge */}
      <Handle
        type="source"
        position={Position.Bottom}
        style={{
          background: theme.palette.primary.main,
          zIndex: 20,
        }}
      />
      <Handle
        type="target"
        position={Position.Top}
        style={{
          background: theme.palette.primary.main,
          zIndex: 20,
        }}
      />
      <dialog ref={dialogRef} style={{ width: "70dvw", height: "80dvh" }}>
        <div style={{ position: "relative", height: "100%", width: "100%" }}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              dialogRef.current?.close();
            }}
            style={{ position: "absolute", top: 10, right: 10, zIndex: 10 }}
          >
            Chiudi
          </Button>
          <EnigmaFlowChart
            cityEscapeId={data.scene.city_escape}
            challenge={showedEnigma}
            height={"100%"}
            filters={data.filters}
            lang={data.activeLang}
            defaultZoom={0.5}
          />
        </div>
      </dialog>
    </Card>
  );
};
